import { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import { reducedStream, streamReducer } from '@ardoq/rxbeach';
import { globalPaneLoader$ } from 'components/GlobalPaneLoader/globalPaneLoader$';
import { componentSelection$ } from 'componentSelection/componentSelection$';
import { ComponentSelection } from '@ardoq/component-selection';
import { isEqual } from 'lodash';
import { Observable, map, distinctUntilChanged } from 'rxjs';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { loadedGraphOperations } from 'traversals/loadedGraphOperations';

type SelectionReloadBuffer = {
  graphSelection: string[];
  lastGraphSelection: string[];
  isLoading: boolean;
  loadedGraph: LoadedGraphWithViewpointMode;
};

/**
 * Manages the component selection state during view reloads.
 *
 * This stream ensures the user's selection is preserved across reloads,
 * even when certain views incorrectly clear the selection. It does this by:
 *
 * **Buffering Updates:** During a reload, all selection updates are
 * buffered. If all components in the selection are still present in the
 * new graph, the update is ignored to maintain the current selection.
 * If not, the selection is reset to a previously stored state or cleared.
 *
 */

const handleComponentSelectionChange = (
  state: SelectionReloadBuffer,
  { graphSelection }: ComponentSelection
) => {
  if (state.isLoading) {
    return {
      ...state,
      lastGraphSelection: graphSelection,
    };
  }

  return {
    ...state,
    graphSelection: graphSelection,
    lastGraphSelection: graphSelection,
  };
};

const handleIsLoadingChange = (
  state: SelectionReloadBuffer,
  isLoading: boolean
) => {
  if (isLoading) {
    return {
      ...state,
      isLoading: true,
    };
  }

  // Case of reloading the view
  if (isSelectionBeforeReloadStillInLoadedGraph(state)) {
    return {
      ...state,
      isLoading: false,
    };
  }

  return {
    ...state,
    graphSelection: isLastGraphSelectionInLoadedGraph(state)
      ? state.lastGraphSelection
      : [],
    isLoading: false,
  };
};

const handleLoadedGraphChange = (
  state: SelectionReloadBuffer,
  loadedGraph: LoadedGraphWithViewpointMode
) => ({
  ...state,
  loadedGraph,
});

const isReloading$ = globalPaneLoader$.pipe(
  map(({ isLoading }) => isLoading),
  distinctUntilChanged()
);

export const componentSelectionReloadBuffered$: Observable<string[]> =
  reducedStream(
    'componentSelectionReloadBuffered$',
    {
      graphSelection: [],
      lastGraphSelection: [],
      isLoading: false,
      loadedGraph: loadedGraphOperations.getEmpty(),
    },
    [
      streamReducer(componentSelection$, handleComponentSelectionChange),
      streamReducer(isReloading$, handleIsLoadingChange),
      streamReducer(loadedGraph$, handleLoadedGraphChange),
    ]
  ).pipe(
    map(({ graphSelection }) => graphSelection),
    distinctUntilChanged((a, b) => isEqual(a, b))
  );

const isSelectionBeforeReloadStillInLoadedGraph = (
  state: SelectionReloadBuffer
) => {
  const scopeComponents = new Set(state.loadedGraph.scopeComponentIds);
  return (
    state.graphSelection.length &&
    // The selection before the reload is still in the current loaded graph.
    state.graphSelection.every(id => scopeComponents.has(id))
  );
};

const isLastGraphSelectionInLoadedGraph = (state: SelectionReloadBuffer) => {
  const scopeComponents = new Set(state.loadedGraph.scopeComponentIds);
  return (
    state.lastGraphSelection.length &&
    state.lastGraphSelection.every(id => scopeComponents.has(id))
  );
};
